<template>
  <div class="payment-method-return-container">
    <!-- 載入中狀態 -->
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>正在處理授權結果...</p>
    </div>

    <!-- 成功狀態 -->
    <div v-else-if="isSuccess" class="success-state">
      <div class="success-icon">✅</div>
      <h2>授權成功</h2>
      <p>您的信用卡已成功綁定</p>
      
      <div class="card-info" v-if="cardMask">
        <p><strong>卡號：</strong>{{ cardMask }}</p>
        <p><strong>卡片品牌：</strong>{{ cardBrand }}</p>
        <p v-if="cardExpiryDate"><strong>有效期限：</strong>{{ cardExpiryDate }}</p>
      </div>

      <button @click="close" class="btn btn-primary">關閉</button>
    </div>

    <!-- 失敗狀態 -->
    <div v-else class="error-state">
      <div class="error-icon">❌</div>
      <h2>授權失敗</h2>
      <p>{{ errorMessage }}</p>
      
      <div class="error-details" v-if="errorCode">
        <p><small>錯誤代碼：{{ errorCode }}</small></p>
      </div>

      <button @click="retry" class="btn btn-secondary">重試</button>
      <button @click="close" class="btn btn-tertiary">關閉</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { postToGhl, postToWindow } from '../utils/ghlMessaging';
import {
  buildSuccessResponsePayload,
  buildErrorResponsePayload,
  buildCloseResponsePayload,
} from '../utils/ghlEventPayloads';

export default {
  name: 'PaymentMethodReturn',
  setup() {
    const isLoading = ref(true);
    const isSuccess = ref(false);
    const errorMessage = ref('');
    const errorCode = ref('');
    let lastStatus = '';
    
    // 卡片資訊
    const cardMask = ref('');
    const cardBrand = ref('');
    const cardExpiryDate = ref('');

    /**
     * 解析 URL 查詢參數
     */
    const parseQuery = () => {
      if (window.paymentMethodReturnParams) {
        const params = window.paymentMethodReturnParams;
        delete window.paymentMethodReturnParams;
        lastStatus = params.Status || '';
        return params;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const params = {
        Status: urlParams.get('Status'),
        MerchantID: urlParams.get('MerchantID'),
        TradeInfo: urlParams.get('TradeInfo'),
        TradeSha: urlParams.get('TradeSha'),
        Version: urlParams.get('Version'),
      };
      lastStatus = params.Status || '';
      return params;
    };

    /**
     * 處理授權回傳結果
     */
    const handleAuthorizationReturn = async () => {
      try {
        isLoading.value = true;

        // 1. 從 URL 取得藍新回傳的參數
        const params = parseQuery();
        
        if (!params.TradeInfo || !params.TradeSha) {
          const errorText = buildStatusMessage(params.Status);
          throw new Error(errorText || '缺少必要參數');
        }

        console.log('[PaymentMethodReturn] 接收藍新回傳:', {
          status: params.Status,
          merchantId: params.MerchantID,
        });

        // 2. 呼叫後端 API 處理回傳資料（僅供顯示）
        const response = await axios.post('/api/payment-method/return', {
          Status: params.Status,
          MerchantID: params.MerchantID,
          TradeInfo: params.TradeInfo,
          TradeSha: params.TradeSha,
          Version: params.Version,
        });

        if (!response.data.success) {
          throw new Error(response.data.message || '處理授權結果失敗');
        }

        const { result } = response.data;

        // 3. 檢查授權狀態
        if (result.Status === 'SUCCESS') {
          isSuccess.value = true;
          
          // 提取卡片資訊
          if (result.Result) {
            cardMask.value = `${result.Result.Card6No?.substring(0, 4) || '****'}****${result.Result.Card4No || '****'}`;
            cardBrand.value = detectCardBrandName(result.Result.Card6No || '');
            
            // 解析官方 TokenLife（yyyy-mm-dd；相容舊 YYMM）
            if (result.Result.TokenLife) {
              const tokenLife = String(result.Result.TokenLife);
              const official = tokenLife.match(/^(\d{4})-(\d{2})-(\d{2})$/);
              if (official) {
                const year = Number(official[1]);
                const month = Number(official[2]);
                const day = Number(official[3]);
                const date = new Date(Date.UTC(year, month - 1, day));
                if (
                  date.getUTCFullYear() === year &&
                  date.getUTCMonth() === month - 1 &&
                  date.getUTCDate() === day
                ) {
                  cardExpiryDate.value = `${official[2]}/${official[1].slice(2)}`;
                }
              } else {
                const legacy = tokenLife.match(/^(\d{2})(0[1-9]|1[0-2])$/);
                if (legacy) cardExpiryDate.value = `${legacy[2]}/${legacy[1]}`;
              }
            }
          }

          console.log('[PaymentMethodReturn] 授權成功');
          
          // 通知父視窗（Add Card on File）
          if (window.opener && !window.opener.closed) {
            postToWindow(window.opener, {
              type: 'authorization_success',
              data: {
                paymentMethodId: result.Result.TokenValue,
              },
            });
          }

          if (window.parent !== window) {
            postToGhl(window.parent, buildSuccessResponsePayload());
          }
        } else {
          throw new Error(result.Message || buildStatusMessage(result.Status) || '授權失敗');
        }

        isLoading.value = false;
      } catch (err) {
        console.error('[PaymentMethodReturn] 處理失敗:', err);
        
        isSuccess.value = false;
        const apiMessage = err.response?.data?.message || err.message || '處理授權結果時發生錯誤';
        const statusMessage = buildStatusMessage(lastStatus);
        if (apiMessage.includes('Failed to decrypt return data') || apiMessage.includes('Missing TradeInfo') || apiMessage.includes('Missing TradeSha')) {
          errorMessage.value = statusMessage || apiMessage;
        } else {
          errorMessage.value = statusMessage || apiMessage;
        }
        errorCode.value = err.response?.data?.errorCode || '';
        
        isLoading.value = false;

        // 通知父視窗失敗
        if (window.opener && !window.opener.closed) {
          postToWindow(window.opener, {
            type: 'authorization_failed',
            data: {
              message: errorMessage.value,
            },
          });
        }

        if (window.parent !== window) {
          postToGhl(window.parent, buildErrorResponsePayload({ description: errorMessage.value }));
        }
      }
    };

    /**
     * 依藍新狀態碼提供更清楚的錯誤訊息
     */
    const buildStatusMessage = (status) => {
      if (!status) return '';
      const mapping = {
        MPG03012: '此商店未啟用約定信用卡/Token 付款功能，請聯絡藍新開通。',
        MPG03007: '查無此商店代號，請確認商店設定或環境是否正確。'
      };
      return mapping[status] || `授權失敗（${status}）`;
    };

    /**
     * 偵測卡片品牌名稱（前端簡化版）
     */
    const detectCardBrandName = (card6Number) => {
      if (!card6Number) return '未知';
      
      const firstDigit = card6Number[0];
      const firstTwoDigits = parseInt(card6Number.substring(0, 2), 10);
      
      if (firstDigit === '4') return 'Visa';
      if ((firstTwoDigits >= 51 && firstTwoDigits <= 55) || (firstTwoDigits >= 22 && firstTwoDigits <= 27)) {
        return 'Mastercard';
      }
      if (card6Number.startsWith('35')) return 'JCB';
      if (card6Number.startsWith('34') || card6Number.startsWith('37')) return 'American Express';
      if (card6Number.startsWith('62')) return 'UnionPay';
      
      return '未知';
    };

    /**
     * 關閉視窗
     */
    const close = () => {
      // 如果是彈出視窗，關閉自己
      if (window.opener) {
        window.close();
      } 
      // 如果是 IFrame，發送關閉事件
      else if (window.parent !== window) {
        postToGhl(window.parent, buildCloseResponsePayload());
      }
      // 否則導向首頁
      else {
        window.location.href = '/';
      }
    };

    /**
     * 重試授權
     */
    const retry = () => {
      // 返回上一頁（AddPaymentMethod）
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.href = '/add-payment-method';
      }
    };

    onMounted(() => {
      console.log('[PaymentMethodReturn] 元件已掛載');
      handleAuthorizationReturn();
    });

    return {
      isLoading,
      isSuccess,
      errorMessage,
      errorCode,
      cardMask,
      cardBrand,
      cardExpiryDate,
      close,
      retry,
    };
  },
};
</script>

<style scoped>
.payment-method-return-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: #f5f5f5;
}

.loading-state,
.success-state,
.error-state {
  text-align: center;
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.success-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.error-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.success-state h2 {
  color: #27ae60;
  margin-bottom: 12px;
}

.error-state h2 {
  color: #e74c3c;
  margin-bottom: 12px;
}

.success-state p,
.error-state p {
  color: #555;
  margin-bottom: 20px;
  line-height: 1.6;
}

.card-info {
  background: #f9f9f9;
  padding: 20px;
  border-radius: 4px;
  margin: 24px 0;
  text-align: left;
}

.card-info p {
  margin: 8px 0;
  color: #333;
}

.error-details {
  background: #fff3cd;
  padding: 12px;
  border-radius: 4px;
  margin: 16px 0;
}

.error-details small {
  color: #856404;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.3s;
  margin: 8px;
}

.btn-primary {
  background: #27ae60;
  color: white;
}

.btn-primary:hover {
  background: #229954;
}

.btn-secondary {
  background: #3498db;
  color: white;
}

.btn-secondary:hover {
  background: #2980b9;
}

.btn-tertiary {
  background: #95a5a6;
  color: white;
}

.btn-tertiary:hover {
  background: #7f8c8d;
}
</style>
